import { useRef, useState } from "react";
import { uploadAdminMedia } from "@/services/adminApi";
import type { MediaLibraryItem } from "@/types";

interface MediaUploaderProps {
  token: string | null;
  onUploaded: (items: MediaLibraryItem[]) => void;
  onClose: () => void;
}

const ACCEPT = "image/png,image/jpeg,image/webp,image/gif,image/avif";

/**
 * "Add New" drop zone — the WordPress-style uploader. Accepts drag-and-drop or
 * the file picker, uploads every selected image to /uploads/library and reports
 * the created records back to the page. Upload happens only on submit.
 */
export default function MediaUploader({ token, onUploaded, onClose }: MediaUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [queue, setQueue] = useState<File[]>([]);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const addFiles = (list: FileList | null) => {
    if (!list) return;
    const images = Array.from(list).filter((file) => file.type.startsWith("image/"));
    if (images.length === 0) {
      setError("Only image files can be uploaded.");
      return;
    }
    setError("");
    setQueue((prev) => [...prev, ...images]);
  };

  const removeFromQueue = (index: number) => {
    setQueue((prev) => prev.filter((_, i) => i !== index));
  };

  const submit = async () => {
    if (queue.length === 0) return;
    setBusy(true);
    setError("");
    try {
      const items = await uploadAdminMedia(queue, token);
      onUploaded(items);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Close uploader"
        onClick={onClose}
        className="absolute inset-0 bg-foreground-950/40 backdrop-blur-sm cursor-pointer"
      ></button>

      <div className="relative z-10 w-full max-w-xl rounded-lg border border-background-200 bg-background-50 p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
              <i className="ri-upload-cloud-2-line text-xl"></i>
            </span>
            <div>
              <h2 className="font-heading text-lg font-bold text-foreground-950">Upload media</h2>
              <p className="text-xs text-foreground-500">PNG, JPG, WEBP, GIF or AVIF · up to 15 MB each</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            addFiles(e.dataTransfer.files);
          }}
          onClick={() => inputRef.current?.click()}
          className={`mt-5 flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed px-6 py-10 text-center transition-colors ${
            dragging ? "border-primary-400 bg-primary-50" : "border-background-300 bg-background-100/60 hover:border-primary-300"
          }`}
        >
          <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-background-200 text-foreground-500">
            <i className="ri-image-add-line text-2xl"></i>
          </span>
          <p className="mt-3 text-sm font-semibold text-foreground-900">Drag images here</p>
          <p className="mt-1 text-xs text-foreground-500">
            or <span className="font-semibold text-primary-700">browse your device</span>
          </p>
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            multiple
            className="hidden"
            onChange={(e) => {
              addFiles(e.target.files);
              e.target.value = "";
            }}
          />
        </div>

        {error && (
          <div className="mt-4 flex items-start gap-2 rounded-md border border-secondary-200 bg-secondary-50 p-3 text-xs text-secondary-900">
            <i className="ri-error-warning-line text-secondary-600"></i>
            <span>{error}</span>
          </div>
        )}

        {queue.length > 0 && (
          <ul className="mt-4 max-h-44 space-y-2 overflow-y-auto">
            {queue.map((file, index) => (
              <li
                key={`${file.name}-${index}`}
                className="flex items-center gap-3 rounded-md border border-background-200 bg-background-100/50 px-3 py-2"
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-background-200 text-foreground-500">
                  <i className="ri-image-line"></i>
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-medium text-foreground-900">{file.name}</p>
                  <p className="text-[11px] text-foreground-400">{Math.round(file.size / 1024)} KB</p>
                </div>
                <button
                  type="button"
                  onClick={() => removeFromQueue(index)}
                  aria-label={`Remove ${file.name}`}
                  className="flex h-7 w-7 items-center justify-center rounded-md text-foreground-500 hover:bg-background-200 cursor-pointer"
                >
                  <i className="ri-close-line"></i>
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-background-300 px-4 py-2.5 text-sm font-semibold text-foreground-700 hover:border-primary-400 whitespace-nowrap cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy || queue.length === 0}
            className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-foreground-950 hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50 whitespace-nowrap cursor-pointer"
          >
            <i className={busy ? "ri-loader-4-line animate-spin" : "ri-upload-line"}></i>
            {busy ? "Uploading…" : `Upload ${queue.length || ""}`.trim()}
          </button>
        </div>
      </div>
    </div>
  );
}